/**
 * Notification Settings Service
 * Handles the notification catalog + settings API calls (Admin only).
 *
 * Named `notificationSettingsService` to avoid clashing with the in-app
 * notifications feature.
 */
import BaseApiService from '../base/BaseApiService';
import type { NotificationSetting, NotificationConfigValue, NotificationType } from '@app-types';

class NotificationSettingsService extends BaseApiService {
  /**
   * Get all notifications from the catalog (with effective config).
   */
  async getNotifications(): Promise<NotificationSetting[]> {
    const response = await this.get<{ success: boolean; data: NotificationSetting[] }>('/admin/notifications');
    return response.data;
  }

  /**
   * Get the dispatchable notification types (code + name only), for filter
   * dropdowns. Excludes config-only catalog entries like Delivery Settings.
   */
  async getNotificationTypes(): Promise<NotificationType[]> {
    const response = await this.get<{ success: boolean; data: NotificationType[] }>('/admin/notifications/types');
    return response.data;
  }

  /**
   * Get a single notification by code.
   */
  async getNotification(code: string): Promise<NotificationSetting> {
    const response = await this.get<{ success: boolean; data: NotificationSetting }>(`/admin/notifications/${code}`);
    return response.data;
  }

  /**
   * Update a notification's config overrides.
   */
  async updateNotification(
    code: string,
    config: Record<string, NotificationConfigValue>
  ): Promise<NotificationSetting> {
    const response = await this.put<{ success: boolean; data: NotificationSetting }>(`/admin/notifications/${code}`, {
      config
    });
    return response.data;
  }

  /**
   * Reset a notification's config to defaults.
   */
  async resetNotification(code: string): Promise<NotificationSetting> {
    const response = await this.post<{ success: boolean; data: NotificationSetting }>(
      `/admin/notifications/${code}/reset`
    );
    return response.data;
  }
}

export const notificationSettingsService = new NotificationSettingsService();
export default notificationSettingsService;
